import { createStore, useStore } from './store';
import { devError, devLog } from './devLog';

/**
 * Everything the admin view shows. Written to from wherever the fact is known
 * (queue, recorder, AR bridge) and read only by the admin screen.
 *
 * This is the 9pm-on-show-night surface. Keep it boring and always correct.
 */
export interface Diagnostics {
  /** Sessions started since page load. */
  sessionCount: number;
  /** Sessions that reached the reveal. */
  completedCount: number;
  queueDepth: number;
  queueSending: boolean;
  uploadsConfirmed: number;
  smsQueued: number;
  lastError: { at: number; where: string; message: string } | null;
  cameraDeviceId: string | null;
  cameraLabel: string | null;
  micDeviceId: string | null;
  micLabel: string | null;
  micGranted: boolean | null;
  recorderMimeType: string | null;
  arSource: 'stub' | 'real';
  /** Rolling log tail, newest last. Capped so it cannot grow unbounded. */
  events: { at: number; message: string }[];
}

const MAX_EVENTS = 60;

export const diagnostics = createStore<Diagnostics>({
  sessionCount: 0,
  completedCount: 0,
  queueDepth: 0,
  queueSending: false,
  uploadsConfirmed: 0,
  smsQueued: 0,
  lastError: null,
  cameraDeviceId: null,
  cameraLabel: null,
  micDeviceId: null,
  micLabel: null,
  micGranted: null,
  recorderMimeType: null,
  arSource: 'stub',
  events: [],
});

export function patchDiagnostics(patch: Partial<Diagnostics>): void {
  diagnostics.set((prev) => ({ ...prev, ...patch }));
}

export function noteEvent(message: string): void {
  const at = Date.now();
  devLog(`[event] ${message}`);
  diagnostics.set((prev) => ({
    ...prev,
    events: [...prev.events, { at, message }].slice(-MAX_EVENTS),
  }));
}

/**
 * Media errors arrive as MediaError, DOMException, or a bare Event, none of
 * which stringify usefully. The admin view is read off a screen in a dark
 * lobby, so "[object MediaError]" is worse than useless.
 */
function describeError(err: unknown): string {
  if (err === null || err === undefined) return 'unknown error';
  if (typeof err === 'string') return err;
  if (err instanceof DOMException) return `${err.name}: ${err.message}`;
  if (err instanceof Error) return err.message;
  if (typeof MediaError !== 'undefined' && err instanceof MediaError) {
    const codes: Record<number, string> = {
      1: 'MEDIA_ERR_ABORTED',
      2: 'MEDIA_ERR_NETWORK',
      3: 'MEDIA_ERR_DECODE',
      4: 'MEDIA_ERR_SRC_NOT_SUPPORTED (asset missing or wrong codec)',
    };
    return `${codes[err.code] ?? `code ${err.code}`}${err.message ? `: ${err.message}` : ''}`;
  }
  if (typeof err === 'object') {
    const maybe = err as { message?: unknown; name?: unknown; type?: unknown };
    if (typeof maybe.message === 'string') return maybe.message;
    if (typeof maybe.type === 'string') return `event: ${maybe.type}`;
    try {
      return JSON.stringify(err).slice(0, 200);
    } catch {
      return Object.prototype.toString.call(err);
    }
  }
  return String(err);
}

export function noteError(where: string, err: unknown): void {
  const message = describeError(err);
  devError(`[error:${where}]`, err);
  const at = Date.now();
  diagnostics.set((prev) => ({
    ...prev,
    lastError: { at, where, message },
    events: [...prev.events, { at, message: `ERROR ${where}: ${message}` }].slice(-MAX_EVENTS),
  }));
}

export function useDiagnostics(): Diagnostics {
  return useStore(diagnostics);
}
